import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import BASE_URL from '../services/api';

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username_or_email: username, password }),
      });

      const result = await res.json();

      if (!res.ok) {
        setError(result.message || 'Invalid username or password');
        return;
      }

      const { data } = result;

      localStorage.setItem('user', JSON.stringify(data));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('userName', data.name);
      localStorage.setItem('username', data.username);
      localStorage.setItem('userRole', data.role || '');
      localStorage.setItem('portals', JSON.stringify(data.portals || []));

      if (data.is_default_password) {
        navigate('/change-password');
      } else {
        navigate('/my-apps');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-white flex items-center justify-center p-4 lg:p-8"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* LEFT: Login form */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
          {/* TN Logo + Heading */}
          <div className="mb-10 text-center lg:text-left">
            <img
              src="Images/tn-logo.png"
              alt="TN Logo"
              className="w-16 h-16 object-contain mb-6 mx-auto lg:mx-0"
            />
            <h2 className="text-4xl font-bold text-black mb-3 tracking-tight">
              ADTW Management Portal
            </h2>
            <p className="text-gray-600 text-lg font-light">
              Single Sign-On for ADW, Tribal Welfare and TAHDCO Portals
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-gray-100 border border-black text-black px-4 py-3 text-sm font-medium">
                {error}
              </div>
            )}

            {/* Username Input */}
            <div>
              <label className="block text-sm font-semibold text-black mb-2.5">
                Email Id
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-500 group-focus-within:text-black transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Enter your username or email"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value.trim());
                    setError('');
                  }}
                  className="w-full pl-12 pr-4 py-4 border border-gray-300 focus:outline-none focus:border-black transition-all text-black placeholder-gray-400 bg-white"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-sm font-semibold text-black mb-2.5">
                Password
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-500 group-focus-within:text-black transition-colors" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  className="w-full pl-12 pr-12 py-4 border border-gray-300 focus:outline-none focus:border-black transition-all text-black placeholder-gray-400 bg-white"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-black transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-4 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed mt-8"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="text-center lg:text-left text-sm text-gray-500 mt-12 font-light">
            © {new Date().getFullYear()} K Labs Technology. All rights reserved.
          </p>
        </div>

        {/* RIGHT: Image */}
        <div className="hidden lg:flex items-center justify-center">
          <img
            src="Images/login1.png"
            alt="ADTW Login"
            className="w-full max-w-2xl object-contain scale-125 origin-center"
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
